import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { ChatPanel } from './components/ChatPanel';
import { VoiceControls } from './components/VoiceControls';
import { AvatarView } from './components/AvatarView';
import { Captions } from './components/Captions';
import { Settings } from './components/Settings';
import { PixelStreamPanel } from './components/PixelStreamPanel';
import { useSession } from './hooks/useSession';
import { useConversation } from './hooks/useConversation';
import { useWebRTC } from './hooks/useWebRTC';
import { useRealtime } from './hooks/useRealtime';
import { PostMessageAPI } from './services/postMessage';
import './App.css';
// Default config - can be overridden via postMessage or data attributes
const getConfig = () => {
    const script = document.querySelector('script[data-tenant-id]');
    if (script) {
        return {
            tenantId: script.getAttribute('data-tenant-id') || 'default',
            userId: script.getAttribute('data-user-id') || undefined,
            authToken: script.getAttribute('data-auth-token') || undefined,
            apiUrl: script.getAttribute('data-api-url') || undefined,
            avatarEnabled: script.getAttribute('data-avatar-enabled') !== 'false',
        };
    }
    const isProdHost = typeof window !== 'undefined' &&
        window.location.hostname.endsWith('defi-oracle.io');
    return {
        tenantId: 'defi-oracle',
        userId: 'guest',
        apiUrl: isProdHost ? 'https://virtual-banker-api.defi-oracle.io' : undefined,
        avatarEnabled: true,
    };
};
export const App = () => {
    const [config] = useState(getConfig());
    const [showSettings, setShowSettings] = useState(false);
    const [showCaptions, setShowCaptions] = useState(true);
    const [avatarEnabled, setAvatarEnabled] = useState(config.avatarEnabled ?? true);
    const [volume, setVolume] = useState(100);
    const [isMuted, setIsMuted] = useState(false);
    const [captionText, setCaptionText] = useState('');
    const postMessage = new PostMessageAPI();
    const { session, loading, error, createSession, endSession } = useSession(config);
    const { avatarStreamUrl } = useRealtime(session);
    const { messages, isListening, isSpeaking, setIsListening, setIsSpeaking, sendMessage, receiveMessage, } = useConversation();
    const { isConnected, remoteStream, initializeWebRTC, closeWebRTC } = useWebRTC();
    // Initialize session on mount
    useEffect(() => {
        createSession();
    }, []);
    // Initialize WebRTC when session is ready
    useEffect(() => {
        if (session && !isConnected) {
            initializeWebRTC();
        }
    }, [session, isConnected]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            endSession();
            closeWebRTC();
        };
    }, []);
    // Send ready event
    useEffect(() => {
        if (session) {
            postMessage.ready();
            postMessage.sessionStarted(session.sessionId);
        }
    }, [session]);
    // Listen for messages from host
    useEffect(() => {
        const unsubscribe = postMessage.on('open', () => {
            // Widget opened
        });
        return unsubscribe;
    }, []);
    const handleSendMessage = (message) => {
        sendMessage(message);
        // TODO: Send to backend via WebRTC or WebSocket
    };
    const handlePushToTalk = () => {
        setIsListening(true);
        // TODO: Start audio capture
    };
    const handleHandsFree = () => {
        setIsListening(true);
        // TODO: Enable continuous listening
    };
    const handleToggleMute = () => {
        setIsMuted(!isMuted);
        // TODO: Mute/unmute audio
    };
    if (loading) {
        return (_jsx("div", { className: "widget-container loading", children: _jsx("div", { className: "loading-spinner", children: "Loading..." }) }));
    }
    if (error) {
        return (_jsxs("div", { className: "widget-container error", children: [_jsxs("div", { className: "error-message", children: ["Error: ", error] }), _jsx("button", { onClick: createSession, children: "Retry" })] }));
    }
    return (_jsxs("div", { className: "widget-container", children: [_jsxs("div", { className: "widget-header", children: [_jsx("h1", { children: "Virtual Banker" }), _jsx("button", { onClick: () => setShowSettings(true), className: "settings-button", "aria-label": "Settings", children: "\u2699\uFE0F" })] }), _jsxs("div", { className: "widget-content", children: [avatarEnabled && (_jsxs("div", { className: "widget-avatar-section", children: [_jsx(PixelStreamPanel, { streamUrl: avatarStreamUrl }), _jsx(AvatarView, { enabled: avatarEnabled && !avatarStreamUrl, videoStream: remoteStream || undefined, onToggle: () => setAvatarEnabled(false) })] })), _jsx("div", { className: "widget-chat-section", children: _jsx(ChatPanel, { messages: messages, onSendMessage: handleSendMessage, isListening: isListening, isSpeaking: isSpeaking, showCaptions: showCaptions, onToggleCaptions: () => setShowCaptions(!showCaptions) }) })] }), _jsx(VoiceControls, { onPushToTalk: handlePushToTalk, onHandsFree: handleHandsFree, isListening: isListening, isMuted: isMuted, onToggleMute: handleToggleMute, volume: volume, onVolumeChange: setVolume }), _jsx(Captions, { text: captionText, visible: showCaptions }), showSettings && (_jsx(Settings, { showCaptions: showCaptions, onToggleCaptions: () => setShowCaptions(!showCaptions), avatarEnabled: avatarEnabled, onToggleAvatar: () => setAvatarEnabled(!avatarEnabled), onClose: () => setShowSettings(false) }))] }));
};
